import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail, viewFile, downloadFile } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowLeft, UserCircle, ShieldCheck, Eye, Download, Check } from "lucide-react";
import { COMPLIANCE_DOC_TYPES } from "@/lib/compliance";
import AddressAutocomplete from "@/components/ui/AddressAutocomplete";
import { useAuth } from "@/context/AuthContext";

const ROLE_TINT = {
  admin: { bg: "#6f4cff22", color: "#a78bfa" },
  allocator: { bg: "#f59e0b22", color: "#f59e0b" },
  client: { bg: "#3b82f622", color: "#60a5fa" },
  driver: { bg: "#19e6c822", color: "#19e6c8" },
};

export default function Team() {
  const { user: me } = useAuth();
  const isSuperadmin = !!me?.is_superadmin;
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [viewingDocs, setViewingDocs] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      // Admin self-manages via Settings — Team page is for drivers & clients.
      // Superadmin additionally manages other admin accounts here.
      setUsers(isSuperadmin ? data : data.filter((u) => u.role !== "admin"));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("User removed");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (viewingDocs) {
    return <DriverDocsView user={viewingDocs} onBack={() => setViewingDocs(null)} />;
  }

  if (adding || editing) {
    return (
      <UserForm
        initial={editing}
        isSuperadmin={isSuperadmin}
        onClose={() => { setAdding(false); setEditing(null); }}
        onSaved={() => { setAdding(false); setEditing(null); load(); }}
      />
    );
  }

  const filtered = filter === "all" ? users : users.filter((u) => u.role === filter);
  const filterTabs = isSuperadmin
    ? ["all", "admin", "allocator", "client", "driver"]
    : ["all", "allocator", "client", "driver"];

  return (
    <div data-testid="team-page">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <div>
          <h1 className="brand-title text-2xl md:text-3xl">Team</h1>
          <p className="text-white/60 text-sm mt-1">Manage admins, drivers and customer accounts.</p>
        </div>
        <button onClick={() => setAdding(true)} className="pill-btn flex items-center gap-2 !text-sm" data-testid="add-user-btn">
          <Plus size={16} /> NEW USER
        </button>
      </div>

      <div className="flex gap-2 mb-5 text-xs">
        {filterTabs.map((r) => (
          <button key={r} onClick={() => setFilter(r)} className={`px-4 py-1.5 rounded-full ${filter === r ? "bg-white text-[#181028] font-semibold" : "bg-white/5 text-white/70 hover:bg-white/10"}`} data-testid={`filter-${r}`}>
            {r.toUpperCase()}
          </button>
        ))}
      </div>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {filtered.length === 0 && <p className="text-white/50">No users.</p>}
        {filtered.map((u) => {
          const tint = ROLE_TINT[u.role] || ROLE_TINT.admin;
          return (
            <div key={u.id} className="rounded-2xl p-5 border border-white/10 bg-white/[0.03]" data-testid={`user-${u.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <span className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: tint.bg, color: tint.color }}>
                    <UserCircle size={20} />
                  </span>
                  <div>
                    <h3 className="brand-title text-base">{u.name}</h3>
                    <p className="text-white/70 text-sm">{u.email}</p>
                  </div>
                </div>
                <span className="text-[10px] px-2 py-0.5 rounded-full uppercase tracking-widest font-semibold" style={{ background: tint.bg, color: tint.color }}>{u.is_superadmin ? "superadmin" : u.role}</span>
              </div>
              <div className="mt-3 text-xs text-white/60 space-y-0.5">
                {u.business_name && <p>Business: <span className="text-white">{u.business_name}</span></p>}
                {u.phone && <p>Phone: <span className="text-white">{u.phone}</span></p>}
                {u.role === "client" && (
                  <>
                    <p>Payment terms: <span className="text-white">{u.payment_terms_days != null ? `${u.payment_terms_days} days` : "Default (14 days)"}</span></p>
                    {u.primary_email && <p>Primary email: <span className="text-white">{u.primary_email}</span></p>}
                    {u.secondary_emails?.length > 0 && <p>CC: <span className="text-white">{u.secondary_emails.join(", ")}</span></p>}
                    {u.abn && <p>ABN: <span className="text-white">{u.abn}</span></p>}
                    {u.business_address && <p>Address: <span className="text-white">{u.business_address}</span></p>}
                  </>
                )}
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button onClick={() => setEditing(u)} className="text-xs px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 flex items-center gap-1" data-testid={`edit-user-${u.id}`}><Pencil size={12} /> Edit</button>
                {u.role === "driver" && (
                  <button onClick={() => setViewingDocs(u)} className="text-xs px-3 py-1.5 rounded-lg flex items-center gap-1" style={{ background: "rgba(25,230,200,0.12)", color: "#19e6c8" }} data-testid={`docs-user-${u.id}`}>
                    <ShieldCheck size={12} /> Documents{Object.keys(u.compliance_docs || {}).length > 0 ? ` (${Object.keys(u.compliance_docs).length})` : ""}
                  </button>
                )}
                {!u.is_superadmin && u.id !== me?.id && (
                  <button onClick={() => remove(u.id)} className="text-xs px-3 py-1.5 rounded-lg bg-red-500/15 hover:bg-red-500/25 text-red-300 flex items-center gap-1" data-testid={`delete-user-${u.id}`}><Trash2 size={12} /> Remove</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function UserForm({ initial, isSuperadmin, onClose, onSaved }) {
  const [data, setData] = useState(initial ? {
    ...initial,
    payment_terms_days: initial.payment_terms_days ?? "",
    password: "",
    primary_email: initial.primary_email || "",
    secondary_emails_input: (initial.secondary_emails || []).join(", "),
    abn: initial.abn || "",
    business_address: initial.business_address || "",
    operating_hours: initial.operating_hours || "",
    notes: initial.notes || "",
  } : {
    name: "", email: "", role: "driver", business_name: "", phone: "", payment_terms_days: "", password: "",
    primary_email: "", secondary_emails_input: "", abn: "", business_address: "", operating_hours: "", notes: "",
  });
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    // Payment terms only apply to customer accounts. Blank → use the default.
    const termsRaw = String(data.payment_terms_days ?? "").trim();
    let terms = null;
    if (data.role === "client" && termsRaw !== "") {
      terms = Number(termsRaw);
      if (!Number.isInteger(terms) || terms < 0) {
        toast.error("Payment terms must be a whole number of days (0 or more)");
        return;
      }
    }
    const secondaryEmails = data.secondary_emails_input
      .split(/[,;\n]/).map((s) => s.trim()).filter(Boolean);
    setBusy(true);
    try {
      if (initial) {
        const payload = { name: data.name, email: data.email, business_name: data.business_name, phone: data.phone };
        if (data.role === "client") {
          payload.payment_terms_days = terms;
          payload.primary_email = data.primary_email || null;
          payload.secondary_emails = secondaryEmails;
          payload.abn = data.abn;
          payload.business_address = data.business_address;
          payload.operating_hours = data.operating_hours;
          payload.notes = data.notes;
        }
        if (data.password) payload.password = data.password;
        await api.put(`/users/${initial.id}`, payload);
      } else {
        if (!data.password) {
          toast.error("Password required for new user");
          return;
        }
        const payload = {
          name: data.name, email: data.email, role: data.role,
          business_name: data.business_name, phone: data.phone, password: data.password,
        };
        if (data.role === "client") {
          if (terms != null) payload.payment_terms_days = terms;
          payload.primary_email = data.primary_email || null;
          payload.secondary_emails = secondaryEmails;
          payload.abn = data.abn;
          payload.business_address = data.business_address;
          payload.operating_hours = data.operating_hours;
          payload.notes = data.notes;
        }
        await api.post("/users", payload);
      }
      toast.success("Saved");
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="fade-up" data-testid="user-form-page">
      <div className="flex items-center gap-3 mb-6">
        <button
          type="button"
          onClick={onClose}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/[0.06] hover:bg-white/10 text-white/70 hover:text-white text-sm font-semibold transition-colors shrink-0"
        >
          <ArrowLeft size={16} /> Back
        </button>
        <h3 className="brand-title text-2xl">{initial ? "Edit User" : "New User"}</h3>
      </div>

      <form onSubmit={save} className="bg-[#15151f] border border-white/10 rounded-2xl p-6 sm:p-8 w-full max-w-2xl">
        <div className="grid gap-4">
          <div>
            <label className="block text-white/85 mb-2 font-medium text-sm">Full Name</label>
            <input className="df-input-rect" placeholder="Full name" value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })} required data-testid="user-name" />
          </div>
          <div>
            <label className="block text-white/85 mb-2 font-medium text-sm">Login Email</label>
            <input className="df-input-rect" type="email" placeholder="Email used to sign in to the portal" value={data.email} onChange={(e) => setData({ ...data, email: e.target.value })} required data-testid="user-email" />
            <p className="text-white/40 text-xs mt-1.5">Used by this user to log in. Changing it changes their login.</p>
          </div>
          {!initial && (
            <div>
              <label className="block text-white/85 mb-2 font-medium text-sm">Role</label>
              <select value={data.role} onChange={(e) => setData({ ...data, role: e.target.value })} className="df-input-rect" data-testid="user-role">
                <option value="driver" className="bg-[#15151f]">Driver</option>
                <option value="client" className="bg-[#15151f]">Client</option>
                <option value="allocator" className="bg-[#15151f]">Allocator</option>
                {isSuperadmin && <option value="admin" className="bg-[#15151f]">Admin</option>}
              </select>
              {isSuperadmin && data.role === "admin" && (
                <p className="text-white/40 text-xs mt-1.5">This admin will get full access to every function in the TMS, same as you — except they can't manage other admin accounts.</p>
              )}
              {data.role === "allocator" && (
                <p className="text-white/40 text-xs mt-1.5">Restricted staff account — Create Job, Job Status and Job History only. Financials, Invoicing, Team and every other admin area stay hidden.</p>
              )}
            </div>
          )}
          <div>
            <label className="block text-white/85 mb-2 font-medium text-sm">Business Name <span className="text-white/40 font-normal">(optional)</span></label>
            <input className="df-input-rect" placeholder="Business name" value={data.business_name || ""} onChange={(e) => setData({ ...data, business_name: e.target.value })} />
          </div>
          <div>
            <label className="block text-white/85 mb-2 font-medium text-sm">Phone <span className="text-white/40 font-normal">(optional)</span></label>
            <input className="df-input-rect" placeholder="Phone number" value={data.phone || ""} onChange={(e) => setData({ ...data, phone: e.target.value })} />
          </div>
          {data.role === "client" && (
            <div>
              <label className="block text-white/85 mb-2 font-medium text-sm">
                Payment Terms <span className="text-white/40 font-normal">(days)</span>
              </label>
              <input
                className="df-input-rect"
                type="number" min="0" step="1"
                placeholder="e.g. 14"
                value={data.payment_terms_days ?? ""}
                onChange={(e) => setData({ ...data, payment_terms_days: e.target.value })}
                data-testid="user-payment-terms"
              />
              <p className="text-white/40 text-xs mt-1.5">Added to the invoice date to set the due date. Leave blank to use the default of 14 days.</p>
            </div>
          )}
          {data.role === "client" && (
            <>
              <div>
                <label className="block text-white/85 mb-2 font-medium text-sm">Primary Email <span className="text-white/40 font-normal">(optional)</span></label>
                <input className="df-input-rect" type="email" placeholder="Where consignment notes & invoices are sent" value={data.primary_email} onChange={(e) => setData({ ...data, primary_email: e.target.value })} data-testid="user-primary-email" />
                <p className="text-white/40 text-xs mt-1.5">CNs and invoices are sent here. Leave blank to use the Login Email instead — set this separately so a login can change (e.g. a staff departure) without affecting billing/CN delivery.</p>
              </div>
              <div>
                <label className="block text-white/85 mb-2 font-medium text-sm">Secondary Emails <span className="text-white/40 font-normal">(optional, CC)</span></label>
                <input className="df-input-rect" placeholder="cc1@company.com, cc2@company.com" value={data.secondary_emails_input} onChange={(e) => setData({ ...data, secondary_emails_input: e.target.value })} data-testid="user-secondary-emails" />
                <p className="text-white/40 text-xs mt-1.5">Comma-separated. CC'd on CN and invoice emails to this client.</p>
              </div>
              <div>
                <label className="block text-white/85 mb-2 font-medium text-sm">ABN <span className="text-white/40 font-normal">(optional)</span></label>
                <input className="df-input-rect" placeholder="e.g. 12 345 678 901" value={data.abn} onChange={(e) => setData({ ...data, abn: e.target.value })} data-testid="user-abn" />
                <p className="text-white/40 text-xs mt-1.5">Shown in the BILL TO section on this client's invoices.</p>
              </div>
              <div>
                <label className="block text-white/85 mb-2 font-medium text-sm">Business Address <span className="text-white/40 font-normal">(optional)</span></label>
                <AddressAutocomplete
                  value={data.business_address}
                  onChange={(v) => setData((d) => ({ ...d, business_address: v }))}
                  onResolved={(p) => setData((d) => ({ ...d, business_address: p.formatted || p.line1 }))}
                  className="df-input-rect"
                  placeholder="Start typing an address…"
                />
              </div>
              <div>
                <label className="block text-white/85 mb-2 font-medium text-sm">Operating Hours <span className="text-white/40 font-normal">(optional)</span></label>
                <input className="df-input-rect" placeholder="e.g. Mon–Fri 8am–5pm" value={data.operating_hours} onChange={(e) => setData({ ...data, operating_hours: e.target.value })} data-testid="user-operating-hours" />
              </div>
              <div>
                <label className="block text-white/85 mb-2 font-medium text-sm">Notes <span className="text-white/40 font-normal">(optional)</span></label>
                <textarea className="df-input-rect resize-none" rows="3" placeholder="Internal notes about this client" value={data.notes} onChange={(e) => setData({ ...data, notes: e.target.value })} data-testid="user-notes" />
              </div>
            </>
          )}
          <div>
            <label className="block text-white/85 mb-2 font-medium text-sm">
              {initial ? "New Password" : "Password"}
              {initial && <span className="text-white/40 font-normal ml-1">(leave blank to keep current)</span>}
            </label>
            <input className="df-input-rect" type="password" placeholder={initial ? "Leave blank to keep current" : "Password"} value={data.password} onChange={(e) => setData({ ...data, password: e.target.value })} data-testid="user-password" />
          </div>
        </div>
        <div className="flex justify-end mt-6">
          <button type="submit" disabled={busy} className="px-6 py-2.5 rounded-lg text-white font-semibold" style={{ background: "linear-gradient(90deg, #2b5fff, #3b82f6)" }} data-testid="save-user-btn">
            {busy ? "Saving..." : "Save User"}
          </button>
        </div>
      </form>
    </div>
  );
}

// Admin view of a driver's uploaded Safety & Compliance documents.
function DriverDocsView({ user, onBack }) {
  const docs = user.compliance_docs || {};

  const open = async (path) => {
    try { await viewFile(path); } catch (e) { toast.error(e.message); }
  };
  const download = async (path, name) => {
    try { await downloadFile(path, name); } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="fade-up" data-testid="driver-docs-page">
      <div className="flex items-center gap-3 mb-6">
        <button type="button" onClick={onBack} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/[0.06] hover:bg-white/10 text-white/70 hover:text-white text-sm font-semibold transition-colors shrink-0">
          <ArrowLeft size={16} /> Back
        </button>
        <div>
          <h3 className="brand-title text-2xl">Compliance Documents</h3>
          <p className="text-white/55 text-sm">{user.name} · {user.email}</p>
        </div>
      </div>

      <div className="space-y-3 max-w-2xl">
        {COMPLIANCE_DOC_TYPES.map(({ key, label }) => {
          const doc = docs[key];
          return (
            <div key={key} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-white text-sm">{label}</p>
                {doc ? (
                  <p className="text-xs text-[#19e6c8] truncate flex items-center gap-1.5 mt-0.5"><Check size={12} /> {doc.name}</p>
                ) : (
                  <p className="text-xs text-white/40 mt-0.5">Not uploaded</p>
                )}
              </div>
              {doc && (
                <>
                  <button onClick={() => open(doc.path)} className="px-3 py-2 rounded-lg text-xs font-semibold flex items-center gap-1" style={{ background: "rgba(59,130,246,0.15)", color: "#60a5fa" }}>
                    <Eye size={13} /> View
                  </button>
                  <button onClick={() => download(doc.path, doc.name)} className="px-3 py-2 rounded-lg text-xs font-semibold flex items-center gap-1" style={{ background: "rgba(25,230,200,0.12)", color: "#19e6c8" }}>
                    <Download size={13} /> Download
                  </button>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
